import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
    SaveIcon as Save,
    ExternalLinkIcon as ExternalLink,
    RefreshIcon as Refresh
} from '../components/Icons';
import {
    Globe,
    Shield,
    CheckCircle2,
    FileText,
    Bot,
    Users,
    Calendar,
    BookOpen,
    Tent,
    Sparkles,
    Check,
    AlertCircle,
    BrainCircuit,
    Code2,
    Eye
} from 'lucide-react';

const AdminLLMs = () => {
    const { user, API_URL } = useContext(AuthContext);
    const { organization } = useSettings();

    // Mode: 'robots' (robots.txt) | 'llms' (llms.txt)
    const [mode, setMode] = useState('robots');

    // Sub-tab: 'editor' | 'preview'
    const [activeTab, setActiveTab] = useState('editor');

    // robots.txt state
    const [robotsContent, setRobotsContent] = useState('');
    const [robotsPreview, setRobotsPreview] = useState('');

    // llms.txt state
    const [llmsContent, setLlmsContent] = useState('');
    const [llmsDynamicOptions, setLlmsDynamicOptions] = useState({
        events: true,
        blogs: true,
        team: true,
        groups: true
    });
    const [llmsPreview, setLlmsPreview] = useState('');

    // Loading & saving status
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    const stammName = organization?.stammName || 'Pfadfinder Stamm';
    const frontendUrl = window.location.origin.replace('dash.', '').replace('3001', '3000');
    const baseUrl = API_URL.replace('/api', '');

    const robotsPresets = [
        {
            name: 'Standard (Empfohlen)',
            desc: 'Erlaubt allen Suchmaschinen und Crawlern den Zugriff auf alle Seiten inklusive Sitemap.',
            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
            template: `# robots.txt für ${stammName}
# ${frontendUrl}

User-agent: *
Allow: /

# Sitemap
Sitemap: ${frontendUrl}/sitemap.xml
`
        },
        {
            name: 'Suchmaschinen ja, KI-Scraper sperren',
            desc: 'Erlaubt Google & Bing, sperrt aber aggressive KI-Scraper (GPTBot, ClaudeBot, Perplexity, etc.).',
            icon: <Bot className="w-5 h-5 text-amber-500" />,
            template: `# robots.txt für ${stammName}
# ${frontendUrl}

User-agent: *
Allow: /

# KI-Scraper & Crawler blockieren
User-agent: GPTBot
Disallow: /

User-agent: ChatGPT-User
Disallow: /

User-agent: ClaudeBot
Disallow: /

User-agent: anthropic-ai
Disallow: /

User-agent: PerplexityBot
Disallow: /

User-agent: CCBot
Disallow: /

User-agent: Bytespider
Disallow: /

# Sitemap
Sitemap: ${frontendUrl}/sitemap.xml
`
        },
        {
            name: 'Alles sperren (Privat / Wartung)',
            desc: 'Blockiert alle Suchmaschinen und Web-Crawler (z. B. für Test- & Staging-Systeme).',
            icon: <Shield className="w-5 h-5 text-rose-500" />,
            template: `# robots.txt für ${stammName}
# ACHTUNG: Alle Crawler gesperrt!

User-agent: *
Disallow: /
`
        }
    ];

    const defaultLlmsTemplate = `# ${stammName}

> Die offizielle Website der ${stammName}.

${stammName} bietet Kindern und Jugendlichen die Möglichkeit, Natur zu erleben, Freundschaften zu schließen und fürs Leben zu lernen.

## Wichtige Links
- [Startseite](${frontendUrl}/): Hauptseite mit Informationen über ${stammName}
- [Blog](${frontendUrl}/blog): Aktuelle Beiträge, Berichte und Neuigkeiten
- [Events](${frontendUrl}/events): Kommende Veranstaltungen, Lager und Aktionen
- [Kontakt](${frontendUrl}/contact): Kontaktformular und Kontaktdaten
- [Impressum](${frontendUrl}/impressum): Rechtliche Angaben
`;

    // Load initial settings
    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            try {
                // 1. Fetch robots
                try {
                    const resRobots = await axios.get(`${API_URL}/settings/robots`);
                    if (resRobots?.data) {
                        const rData = resRobots.data;
                        if (typeof rData === 'string') {
                            setRobotsContent(rData);
                        } else if (typeof rData === 'object') {
                            setRobotsContent(rData.content || robotsPresets[0].template);
                        }
                    } else {
                        setRobotsContent(robotsPresets[0].template);
                    }
                } catch {
                    setRobotsContent(robotsPresets[0].template);
                }

                // 2. Fetch llms
                try {
                    const resLlms = await axios.get(`${API_URL}/settings/llms`);
                    if (resLlms?.data) {
                        const lData = resLlms.data;
                        if (typeof lData === 'string') {
                            setLlmsContent(lData);
                        } else if (typeof lData === 'object') {
                            setLlmsContent(lData.content || defaultLlmsTemplate);
                            if (lData.dynamicOptions || lData.options) {
                                const opts = lData.dynamicOptions || lData.options || {};
                                setLlmsDynamicOptions({
                                    events: opts.events !== undefined ? !!opts.events : true,
                                    blogs: opts.blogs !== undefined ? !!opts.blogs : true,
                                    team: (opts.team ?? opts.leaders) !== undefined ? !!(opts.team ?? opts.leaders) : true,
                                    groups: opts.groups !== undefined ? !!opts.groups : true
                                });
                            }
                        }
                    } else {
                        setLlmsContent(defaultLlmsTemplate);
                    }
                } catch {
                    setLlmsContent(defaultLlmsTemplate);
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, [API_URL]);

    const fetchLivePreview = useCallback(async () => {
        setIsLoadingPreview(true);
        try {
            if (mode === 'robots') {
                const res = await axios.get(`${baseUrl}/robots.txt?t=${Date.now()}`, {
                    responseType: 'text'
                });
                setRobotsPreview(res.data);
            } else {
                const res = await axios.get(`${baseUrl}/llms.txt?t=${Date.now()}`, {
                    responseType: 'text'
                });
                setLlmsPreview(res.data);
            }
        } catch (e) {
            console.error('Preview error:', e);
            if (mode === 'robots') {
                setRobotsPreview('Fehler beim Abrufen der Live-robots.txt.');
            } else {
                setLlmsPreview('Fehler beim Abrufen der Live-llms.txt.');
            }
        } finally {
            setIsLoadingPreview(false);
        }
    }, [baseUrl, mode]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'preview') {
            fetchLivePreview();
        }
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setMessage('');
        setIsError(false);
        if (activeTab === 'preview') {
            setTimeout(fetchLivePreview, 50);
        }
    };

    const handleLlmsOptionToggle = (key) => {
        setLlmsDynamicOptions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSaveRobots = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);

        try {
            await axios.put(`${API_URL}/settings/robots`, {
                content: robotsContent
            }, {
                headers: { Authorization: `Bearer ${user.token}` }
            });

            setMessage('robots.txt erfolgreich gespeichert.');
            if (activeTab === 'preview') {
                fetchLivePreview();
            }
        } catch (err) {
            console.error('Error saving robots.txt:', err);
            setMessage('Fehler beim Speichern der robots.txt.');
            setIsError(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveLlms = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);

        try {
            await axios.put(`${API_URL}/settings/llms`, {
                content: llmsContent,
                dynamicOptions: llmsDynamicOptions
            }, {
                headers: { Authorization: `Bearer ${user.token}` }
            });

            setMessage('llms.txt & KI-Optionen erfolgreich gespeichert.');
            if (activeTab === 'preview') {
                fetchLivePreview();
            }
        } catch (err) {
            console.error('Error saving llms.txt:', err);
            setMessage('Fehler beim Speichern der llms.txt.');
            setIsError(true);
        } finally {
            setIsSaving(false);
        }
    };

    const applyRobotsPreset = (template) => {
        if (window.confirm('Möchtest du diese Vorlage in den robots.txt Editor übernehmen?')) {
            setRobotsContent(template);
        }
    };

    const applyLlmsDefault = () => {
        if (window.confirm('Möchtest du die Standard-Vorlage für llms.txt wiederherstellen?')) {
            setLlmsContent(defaultLlmsTemplate);
        }
    };

    if (isLoading) {
        return <div className="text-surface-onVariant text-center py-10">Lade SEO & KI-Einstellungen...</div>;
    }

    const publicRobotsUrl = `${baseUrl}/robots.txt`;
    const publicLlmsUrl = `${baseUrl}/llms.txt`;

    const llmsOptionCards = [
        {
            key: 'team',
            title: 'Personen & Team (Leitungen)',
            desc: 'Fügt sichtbare Gruppenleitungen, Rollen, Kontaktdaten und Profil-Links strukturiert für KI-Modelle ein.',
            icon: <Users className="w-5 h-5 text-indigo-500" />,
            badgeColor: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
        },
        {
            key: 'groups',
            title: 'Gruppen & Stufen',
            desc: 'Fügt aktive Stufen, Gruppenstunden-Zeiten, Treffpunkte und Beschreibungen strukturiert ein.',
            icon: <Tent className="w-5 h-5 text-emerald-500" />,
            badgeColor: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        },
        {
            key: 'events',
            title: 'Termine & Veranstaltungen',
            desc: 'Fügt aktuelle und zukünftige öffentliche Termine, Uhrzeiten, Orte und Anmeldelinks ein.',
            icon: <Calendar className="w-5 h-5 text-amber-500" />,
            badgeColor: 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
        },
        {
            key: 'blogs',
            title: 'Blogbeiträge & Berichte',
            desc: 'Fügt veröffentlichte Berichte und Blogbeiträge mit Auszügen und Verlinkungen ein.',
            icon: <BookOpen className="w-5 h-5 text-blue-500" />,
            badgeColor: 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
        }
    ];

    const activeLlmsCount = Object.values(llmsDynamicOptions).filter(Boolean).length;

    return (
        <div className="space-y-6 max-w-4xl animate-fade-in pb-16">
            {/* Header */}
            <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-headline-small font-bold text-surface-on mb-1 flex items-center gap-2">
                        <Globe className="w-6 h-6 text-primary" />
                        Robots & KI-Metadaten
                    </h2>
                    <p className="text-body-medium text-surface-onVariant">
                        Konfiguriere Anweisungen für klassische Web-Crawler (robots.txt) und strukturierte Kontextdaten für moderne KI-Suchmaschinen (llms.txt).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href={publicRobotsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="md3-btn-tonal text-xs py-2 px-3 flex items-center gap-1.5 shrink-0"
                    >
                        <ExternalLink style={{ fontSize: 16 }} />
                        /robots.txt
                    </a>
                    <a
                        href={publicLlmsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="md3-btn-tonal text-xs py-2 px-3 flex items-center gap-1.5 shrink-0"
                    >
                        <ExternalLink style={{ fontSize: 16 }} />
                        /llms.txt
                    </a>
                </div>
            </header>

            {message && (
                <div className={`p-4 rounded-2xl text-body-medium font-medium flex items-center gap-2 ${isError ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'}`}>
                    {isError ? <AlertCircle className="w-5 h-5 shrink-0" /> : <Check className="w-5 h-5 shrink-0" />}
                    <span>{message}</span>
                </div>
            )}

            {/* Mode Switcher: robots.txt vs llms.txt */}
            <div className="flex bg-surface-containerLow p-1.5 rounded-2xl border border-outlineVariant/30 gap-1.5">
                <button
                    onClick={() => handleModeChange('robots')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                        mode === 'robots'
                            ? 'bg-surface text-primary shadow-xs ring-1 ring-outlineVariant/20'
                            : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'
                    }`}
                >
                    <Bot className="w-4 h-4" />
                    <span>robots.txt (Suchmaschinen)</span>
                </button>
                <button
                    onClick={() => handleModeChange('llms')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                        mode === 'llms'
                            ? 'bg-surface text-primary shadow-xs ring-1 ring-outlineVariant/20'
                            : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-container'
                    }`}
                >
                    <BrainCircuit className="w-4 h-4" />
                    <span>llms.txt (KI-Assistenten)</span>
                </button>
            </div>

            {/* Content for robots.txt */}
            {mode === 'robots' && (
                <div className="space-y-6">
                    {/* Presets */}
                    <div className="space-y-2">
                        <label className="text-label-large font-bold text-surface-on flex items-center gap-1.5">
                            <FileText className="w-4 h-4 text-primary" />
                            robots.txt Schnell-Vorlagen
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {robotsPresets.map((preset, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => applyRobotsPreset(preset.template)}
                                    className="p-4 bg-surface-containerLow hover:bg-surface-container border border-outlineVariant/30 rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-xs flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="flex items-center gap-2 font-bold text-sm text-surface-on mb-1">
                                            {preset.icon}
                                            {preset.name}
                                        </div>
                                        <p className="text-xs text-surface-onVariant leading-relaxed">
                                            {preset.desc}
                                        </p>
                                    </div>
                                    <span className="text-[11px] font-bold text-primary mt-3 inline-block">
                                        Vorlage anwenden →
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-outlineVariant/30 gap-4">
                        <button
                            onClick={() => handleTabChange('editor')}
                            className={`pb-3 px-2 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
                                activeTab === 'editor'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Code2 className="w-4 h-4" />
                            robots.txt Editor
                        </button>
                        <button
                            onClick={() => handleTabChange('preview')}
                            className={`pb-3 px-2 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
                                activeTab === 'preview'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Eye className="w-4 h-4" />
                            Live-Vorschau (/robots.txt)
                        </button>
                    </div>

                    {activeTab === 'editor' ? (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                                <div className="flex justify-between items-center">
                                    <label className="text-label-large font-bold text-surface-on">
                                        Inhalt der robots.txt (Standard RFC 9309)
                                    </label>
                                    <span className="text-xs text-surface-onVariant font-mono">
                                        {robotsContent.length} Zeichen · {robotsContent.split('\n').length} Zeilen
                                    </span>
                                </div>
                                <textarea
                                    value={robotsContent}
                                    onChange={(e) => setRobotsContent(e.target.value)}
                                    className="w-full font-mono text-sm leading-relaxed p-4 rounded-2xl border border-outlineVariant/40 bg-surface-containerLowest text-surface-on focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary transition-all min-h-[300px] resize-y"
                                    placeholder="User-agent: *&#10;Allow: /&#10;&#10;Sitemap: https://.../sitemap.xml"
                                    spellCheck={false}
                                />
                            </div>

                            <div className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/20 text-xs text-surface-onVariant leading-relaxed space-y-1">
                                <p className="font-bold text-surface-on">Wichtige Hinweise für eine fehlerfreie robots.txt:</p>
                                <p>• <code>robots.txt</code> akzeptiert ausschließlich gültige Crawler-Direktiven (z. B. <code>User-agent</code>, <code>Allow</code>, <code>Disallow</code>, <code>Sitemap</code>).</p>
                                <p>• <code>User-agent: *</code> gilt für alle Crawler.</p>
                                <p>• <code>Allow: /</code> erlaubt die vollständige Indexierung durch Google, Bing und andere Suchmaschinen.</p>
                                <p>• <code>Sitemap: {frontendUrl}/sitemap.xml</code> hilft Suchmaschinen, alle Unterseiten und Termine direkt zu finden.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-body-small text-surface-onVariant">
                                    Live-Inhalt direkt von <code>/robots.txt</code>:
                                </span>
                                <button
                                    onClick={fetchLivePreview}
                                    disabled={isLoadingPreview}
                                    className="md3-btn-tonal text-xs py-1.5 px-3 flex items-center gap-1.5"
                                >
                                    <Refresh style={{ fontSize: 16 }} className={isLoadingPreview ? 'animate-spin' : ''} />
                                    Aktualisieren
                                </button>
                            </div>

                            <pre className="p-5 bg-surface-containerLow border border-outlineVariant/30 rounded-2xl font-mono text-xs text-surface-on leading-relaxed overflow-x-auto whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                                {isLoadingPreview ? 'Lade Vorschau...' : robotsPreview}
                            </pre>
                        </div>
                    )}

                    <div className="flex justify-end pt-4 border-t border-outlineVariant/30">
                        <button
                            onClick={handleSaveRobots}
                            disabled={isSaving}
                            className="md3-btn-filled h-12 px-8 flex items-center gap-2 rounded-2xl font-bold shadow-xs"
                        >
                            <Save style={{ fontSize: 20 }} />
                            {isSaving ? 'Speichert...' : 'robots.txt speichern'}
                        </button>
                    </div>
                </div>
            )}

            {/* Content for llms.txt */}
            {mode === 'llms' && (
                <div className="space-y-6">
                    {/* Dynamic Options */}
                    <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                            <label className="text-label-large font-bold text-surface-on flex items-center gap-1.5">
                                <Sparkles className="w-4 h-4 text-primary" />
                                Dynamische Stammesdaten für KI-Modelle freischalten
                            </label>
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary-container text-primary-onContainer self-start sm:self-auto">
                                {activeLlmsCount} von 4 aktiv
                            </span>
                        </div>
                        <p className="text-xs text-surface-onVariant leading-relaxed">
                            Aktivierte Bereiche werden in Echtzeit aus der Datenbank generiert und automatisch im Markdown-Format an <code>/llms.txt</code> angehängt. KI-Assistenten wie ChatGPT Search, Claude, Perplexity und Google Gemini erfassen diese Daten automatisch.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            {llmsOptionCards.map((card) => {
                                const isChecked = !!llmsDynamicOptions[card.key];
                                return (
                                    <div
                                        key={card.key}
                                        onClick={() => handleLlmsOptionToggle(card.key)}
                                        className={`p-4 rounded-2xl border transition-all duration-200 cursor-pointer flex items-start justify-between gap-3 ${
                                            isChecked
                                                ? 'bg-surface-containerHigh border-primary shadow-xs ring-1 ring-primary/20'
                                                : 'bg-surface-containerLow hover:bg-surface-container border-outlineVariant/30'
                                        }`}
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <div className={`p-1.5 rounded-lg ${card.badgeColor}`}>
                                                    {card.icon}
                                                </div>
                                                <span className="font-bold text-sm text-surface-on">
                                                    {card.title}
                                                </span>
                                            </div>
                                            <p className="text-xs text-surface-onVariant leading-relaxed pr-2">
                                                {card.desc}
                                            </p>
                                        </div>
                                        <div className="pt-1">
                                            <div
                                                className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ease-in-out ${
                                                    isChecked ? 'bg-primary justify-end' : 'bg-surface-containerHighest justify-start'
                                                }`}
                                            >
                                                <div
                                                    className="bg-white w-4 h-4 rounded-full shadow-xs"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-outlineVariant/30 gap-4">
                        <button
                            onClick={() => handleTabChange('editor')}
                            className={`pb-3 px-2 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
                                activeTab === 'editor'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Code2 className="w-4 h-4" />
                            llms.txt Basis-Markdown
                        </button>
                        <button
                            onClick={() => handleTabChange('preview')}
                            className={`pb-3 px-2 font-bold text-sm border-b-2 transition-all flex items-center gap-1.5 ${
                                activeTab === 'preview'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Eye className="w-4 h-4" />
                            Live-Vorschau (/llms.txt)
                        </button>
                    </div>

                    {activeTab === 'editor' ? (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-2">
                                <div className="flex justify-between items-center">
                                    <label className="text-label-large font-bold text-surface-on">
                                        Basis-Markdown (Einführung & Wichtige Links)
                                    </label>
                                    <button
                                        onClick={applyLlmsDefault}
                                        className="text-xs text-primary font-bold hover:underline"
                                    >
                                        Standard-Vorlage laden
                                    </button>
                                </div>
                                <textarea
                                    value={llmsContent}
                                    onChange={(e) => setLlmsContent(e.target.value)}
                                    className="w-full font-mono text-sm leading-relaxed p-4 rounded-2xl border border-outlineVariant/40 bg-surface-containerLowest text-surface-on focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary transition-all min-h-[300px] resize-y"
                                    placeholder="# Stamm Name&#10;&#10;> Kurzbeschreibung&#10;&#10;## Wichtige Links&#10;- [Startseite](...)"
                                    spellCheck={false}
                                />
                            </div>

                            <div className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/20 text-xs text-surface-onVariant leading-relaxed space-y-1">
                                <p className="font-bold text-surface-on">Was ist <code>llms.txt</code>?</p>
                                <p>• <code>/llms.txt</code> ist der neue Standard (llmstxt.org) für AI-Suchmaschinen (z. B. ChatGPT, Claude, Perplexity), um Webseiten strukturiert zu verstehen.</p>
                                <p>• Das Format ist sauberes Markdown mit Überschriften, Links und Kurzbeschreibungen.</p>
                                <p>• Deine aktivierten Zusatzoptionen (Events, Blog, Team, Gruppen) werden automatisch in Echtzeit dynamisch angehängt.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-body-small text-surface-onVariant">
                                    Vollständige Live-Generierung inklusive aller aktiven Zusatzoptionen auf <code>/llms.txt</code>:
                                </span>
                                <button
                                    onClick={fetchLivePreview}
                                    disabled={isLoadingPreview}
                                    className="md3-btn-tonal text-xs py-1.5 px-3 flex items-center gap-1.5"
                                >
                                    <Refresh style={{ fontSize: 16 }} className={isLoadingPreview ? 'animate-spin' : ''} />
                                    Aktualisieren
                                </button>
                            </div>

                            <pre className="p-5 bg-surface-containerLow border border-outlineVariant/30 rounded-2xl font-mono text-xs text-surface-on leading-relaxed overflow-x-auto whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                                {isLoadingPreview ? 'Lade Vorschau...' : llmsPreview}
                            </pre>
                        </div>
                    )}

                    <div className="flex justify-end pt-4 border-t border-outlineVariant/30">
                        <button
                            onClick={handleSaveLlms}
                            disabled={isSaving}
                            className="md3-btn-filled h-12 px-8 flex items-center gap-2 rounded-2xl font-bold shadow-xs"
                        >
                            <Save style={{ fontSize: 20 }} />
                            {isSaving ? 'Speichert...' : 'llms.txt speichern'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLLMs;